import { ButtonPrimary, ButtonSecondary } from "@nova/component-btn"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { InputOutlined, TextAreaOutlined } from "@nova/ds-component-input"
import { SelectOutlined } from "@nova/component-select"
import { AlertContainer } from "@nova/component-alert-container"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { CustomFieldAnswer, CustomFieldSpec, MissionProvider } from "@nova/schemas"
import { FunctionComponent, useState } from "react"
import { useAppDispatch } from "integrations/redux/hooks"
import { setGlobalNotificationContext } from "user/profile/slice"
import { Divider } from "components/Divider"
import { useCustomFieldDefinition } from "../missions/linked-layers/CustomFieldsQueries"
import { useBulkEditMissionDetails } from "./PilotTrackingMissionQueries"
import { FlightPathMapPreview, LatLng } from "./FlightPathMapPreview"
import { pluralize } from "@nova/util-strings"

const EditCustomFieldInput: FunctionComponent<{
    field: CustomFieldSpec
    value: CustomFieldAnswer["value"] | undefined
    onChange: (value: CustomFieldAnswer["value"]) => void
}> = ({ field, value, onChange }) => {
    switch (field.type) {
        case "text":
            return (
                <InputOutlined
                    label={field.label}
                    value={typeof value === "string" ? value : ""}
                    onChange={(e) => onChange(e.currentTarget.value)}
                />
            )
        case "yes-no":
            return (
                <div className="flex flex-col gap-2 w-full">
                    <label className="text-gray-100">{field.label}</label>
                    <SelectOutlined<boolean | undefined>
                        value={typeof value === "boolean" ? value : undefined}
                        options={[
                            { label: "Yes", value: true },
                            { label: "No", value: false },
                        ]}
                        onChange={(v) => v !== undefined && onChange(v)}
                    />
                </div>
            )
        case "multiple-choice":
            return (
                <div className="flex flex-col gap-2 w-full">
                    <label className="text-gray-100">{field.label}</label>
                    <SelectOutlined<string | undefined>
                        value={typeof value === "string" ? value : undefined}
                        options={field.options.map((o) => ({ label: o, value: o }))}
                        onChange={(v) => v !== undefined && onChange(v)}
                        placeholder="Select"
                    />
                </div>
            )
    }
}

export const EditFlightDetailsModal: FunctionComponent<{
    missionIds: string[]
    syncedCount: number
    onConfirm: () => void
    onCancel: () => void
}> = ({ missionIds, syncedCount, onConfirm, onCancel }) => {
    const dispatch = useAppDispatch()
    const { data: customFieldDefinition } = useCustomFieldDefinition()
    const customFields = customFieldDefinition?.form ?? []
    const { editDetails } = useBulkEditMissionDetails(missionIds)
    const isTakeoffLocationLocked = syncedCount > 0 && syncedCount === missionIds.length

    const [customAnswers, setCustomAnswers] = useState<Map<string, CustomFieldAnswer["value"]>>(
        new Map()
    )
    const [coordsInput, setCoordsInput] = useState("")
    const [takeoffCoords, setTakeoffCoords] = useState<LatLng | null>(null)
    const [notes, setNotes] = useState("")
    const [isWarningDismissed, setIsWarningDismissed] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    const setCustomAnswer = (id: string, value: CustomFieldAnswer["value"]) =>
        setCustomAnswers((prev) => new Map(prev).set(id, value))

    const handleCoordsInputChange = (raw: string) => {
        setCoordsInput(raw)
        const [latStr, lngStr] = raw.split(",").map((s) => s.trim())
        const lat = parseFloat(latStr)
        const lng = parseFloat(lngStr)
        if (!isNaN(lat) && !isNaN(lng)) setTakeoffCoords({ lat, lng })
    }

    const handleMapMove = (next: LatLng) => {
        const rounded = { lat: Number(next.lat.toFixed(5)), lng: Number(next.lng.toFixed(5)) }
        setTakeoffCoords(rounded)
        setCoordsInput(`${rounded.lat}, ${rounded.lng}`)
    }

    const handleSubmit = async () => {
        const customFieldAnswers: CustomFieldAnswer[] = customFields
            .filter((field) => {
                const value = customAnswers.get(field.id)
                return value !== undefined && value !== ""
            })
            .map((field) => ({ id: field.id, value: customAnswers.get(field.id)! }))

        setIsSaving(true)
        try {
            await editDetails({
                notes: notes || undefined,
                takeoffLatitude: takeoffCoords?.lat,
                takeoffLongitude: takeoffCoords?.lng,
                customFieldAnswers: customFieldAnswers.length ? customFieldAnswers : undefined,
            })
            dispatch(
                setGlobalNotificationContext({
                    message: `Details updated for ${pluralize({ count: missionIds.length, str: "flight" })}.`,
                    status: GlobalNotificationStatus.success,
                    timeoutInMs: 3000,
                })
            )
            onConfirm()
        } catch {
            dispatch(
                setGlobalNotificationContext({
                    message: "Failed to update flight details. Please try again.",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 5000,
                })
            )
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <HighPriorityNovaModalContainer
            title="Edit Flight Details"
            closeModal={onCancel}
            className="w-[600px] max-h-wallboard-content"
            hideTitleUnderline
        >
            <div className="flex flex-col gap-4 shrink min-h-0">
                <ScrollableArea
                    shouldPreventScrollbarShifting
                    className="flex flex-col gap-6 h-full pr-3 pt-2"
                >
                    <div className="flex flex-col gap-6 h-full pr-3 pt-2 pl-px">
                        {syncedCount > 0 && !isWarningDismissed && (
                            <AlertContainer
                                type="warning"
                                title={
                                    syncedCount === missionIds.length
                                        ? "Takeoff Location is locked"
                                        : `${syncedCount} of ${missionIds.length} Flight's Takeoff Location is Locked`
                                }
                                body="Takeoff location is set by the synced flight log and can't be changed."
                                onClose={() => setIsWarningDismissed(true)}
                            />
                        )}

                        {customFields.length > 0 && (
                            <div className="flex flex-col gap-4">
                                <h4 className="text-white">Custom Fields</h4>
                                <div className="grid grid-cols-2 gap-4">
                                    {customFields.map((field) => (
                                        <EditCustomFieldInput
                                            key={field.id}
                                            field={field}
                                            value={customAnswers.get(field.id)}
                                            onChange={(value) => setCustomAnswer(field.id, value)}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        <Divider />

                        {!isTakeoffLocationLocked && (
                            <>
                                <div className="flex flex-col gap-3">
                                    <div className="flex flex-col gap-1">
                                        <h4 className="text-gray-100">Takeoff Location</h4>
                                        <h5 className="text-gray-100">
                                            Set your takeoff coordinates or drag the map to position
                                            the pin.
                                        </h5>
                                    </div>
                                    <InputOutlined
                                        placeholder="Latitude, Longitude"
                                        value={coordsInput}
                                        onChange={(e) =>
                                            handleCoordsInputChange(e.currentTarget.value)
                                        }
                                    />
                                    <div className="rounded-lg h-[200px]">
                                        <FlightPathMapPreview
                                            takeoffCoords={takeoffCoords}
                                            onTakeoffChange={handleMapMove}
                                        />
                                    </div>
                                </div>

                                <Divider />
                            </>
                        )}

                        <TextAreaOutlined
                            label="Notes"
                            value={notes}
                            onChange={(e) => setNotes(e.currentTarget.value)}
                            rows={3}
                        />

                        <div className="sticky bottom-0 flex items-center gap-3 justify-end w-full mt-2 pt-3 bg-gray-900">
                            <ButtonSecondary
                                title="Cancel"
                                onClick={onCancel}
                                disabled={isSaving}
                            />
                            <ButtonPrimary
                                title="Save Details"
                                onClick={() => void handleSubmit()}
                                isLoading={isSaving}
                            />
                        </div>
                    </div>
                </ScrollableArea>
            </div>
        </HighPriorityNovaModalContainer>
    )
}

export const countSyncedMissions = (
    selectedData: { id: string; provider: string | null }[]
): number => selectedData.filter((d) => d.provider !== MissionProvider.NovaWebApp).length
